import { useState, useEffect } from 'react';
import { FiltersQueryParamsStates, TalentSearchQueryParams } from '@type/filters';
import { sortObjectKeys } from '@utils/objects';


// Interface
// **************************************
interface FiltersSimilarSearchProps extends FiltersQueryParamsStates<TalentSearchQueryParams> {
  isEmpty?: boolean,
}


/**
 * @description A hook to manage the similar search state for the filters.
 * - queryParams: *object* - the current query params
 * - setQueryParams: *function* - a function to update the query params
 * - isEmpty: *boolean* - if true, the search has no results and we trigger the similar search. Pass the isEmpty prop from SWR.
 */
export const useFiltersSimilarSearch = (props: FiltersSimilarSearchProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    isEmpty,
  } = props;


  // Keep track of the similar search active state
  // **************************************
  const [similarSearchActive, setSimilarSearchActive] = useState(Object.hasOwn(queryParams, 'similarSearch'));


  // Similar search. We activate the similar search if
  // 1. there are no results (isEmpty is true)
  // 2. and the similar search is not already active.
  // **************************************
  useEffect(() => {
    // activate the state
    if (isEmpty && !queryParams?.['similarSearch' as keyof typeof queryParams] && Object.keys(queryParams).length > 0) {
      setSimilarSearchActive(true);
      setQueryParams((prev) => (sortObjectKeys({ ...prev, similarSearch: 1 })));
    }

    // clear the state
    if (!isEmpty && !queryParams?.['similarSearch' as keyof typeof queryParams]) {
      setSimilarSearchActive(false);
    }
  }, [isEmpty, queryParams, setQueryParams]);


  // Return
  // **************************************
  return {
    similarSearchActive,
  };
};
